import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { format } from 'date-fns';

const ACTION_CONFIG = {
  'auth.login':                    { label: 'Logged in',                 category: 'auth',   color: '#6bcb77' },
  'auth.login_failed':             { label: 'Failed login attempt',      category: 'auth',   color: '#e05a4e' },
  'prediction.saved':              { label: 'Prediction saved',          category: 'game',   color: 'var(--volt)' },
  'winner.set':                    { label: 'Winner pick set',           category: 'game',   color: 'var(--volt)' },
  'default.set':                   { label: 'Default score set',         category: 'game',   color: 'var(--volt)' },
  'profile.updated':               { label: 'Profile updated',           category: 'game',   color: 'var(--volt)' },
  'admin.lock_toggled':            { label: 'Predictions lock toggled',  category: 'admin',  color: 'var(--spark-bright)' },
  'admin.result_set':              { label: 'Match result entered',      category: 'admin',  color: 'var(--spark-bright)' },
  'admin.results_uploaded':        { label: 'Results bulk uploaded',     category: 'admin',  color: 'var(--spark-bright)' },
  'admin.predictions_uploaded':    { label: 'Predictions bulk uploaded', category: 'admin',  color: 'var(--spark-bright)' },
  'admin.tournament_winner_set':   { label: 'Tournament winner set',     category: 'admin',  color: 'var(--spark-bright)' },
  'admin.user_invited':            { label: 'Player invited',            category: 'admin',  color: 'var(--spark-bright)' },
  'admin.user_role_changed':       { label: 'Role changed',              category: 'admin',  color: 'var(--spark-bright)' },
  'admin.user_password_reset':     { label: 'Password reset sent',       category: 'admin',  color: 'var(--spark-bright)' },
  'admin.user_deleted':            { label: 'Player deleted',            category: 'admin',  color: '#e05a4e' },
  'admin.friendly_set':            { label: 'Test match configured',     category: 'admin',  color: 'var(--spark-bright)' },
  'admin.knockout_teams_set':      { label: 'Knockout teams set',        category: 'admin',  color: 'var(--spark-bright)' },
  'admin.league_created':          { label: 'League created',            category: 'admin',  color: 'var(--spark-bright)' },
  'admin.league_member_invited':   { label: 'Player invited to league',  category: 'admin',  color: 'var(--spark-bright)' },
  'admin.league_member_added':     { label: 'Player added to league',    category: 'admin',  color: 'var(--spark-bright)' },
  'admin.league_member_removed':   { label: 'Player removed from league',category: 'admin',  color: '#e05a4e' },
  'admin.league_renamed':          { label: 'League renamed',            category: 'admin',  color: 'var(--spark-bright)' },
  'admin.league_deleted':          { label: 'League deleted',            category: 'admin',  color: '#e05a4e' },
  'admin.placeholder_created':     { label: 'Placeholder created',       category: 'admin',  color: 'var(--spark-bright)' },
  'admin.placeholder_invited':     { label: 'Placeholder invited',       category: 'admin',  color: 'var(--spark-bright)' },
  'admin.placeholder_deleted':     { label: 'Placeholder deleted',       category: 'admin',  color: '#e05a4e' },
  'admin.proxy_prediction':        { label: 'Proxy prediction entered',  category: 'admin',  color: 'var(--spark-bright)' },
  'auth.placeholder_merged':       { label: 'Placeholder account merged',category: 'auth',   color: '#6bcb77' },
  'results.auto_synced':           { label: 'Auto-sync complete',        category: 'system', color: 'var(--text-muted)' },
};

const actionCategory = (action) =>
  ACTION_CONFIG[action]?.category ?? (action?.startsWith('admin.') ? 'admin' : 'game');

function formatDetail(action, detail) {
  try {
    const d = JSON.parse(detail || '{}');
    if (action === 'auth.login')         return d.email || '';
    if (action === 'auth.login_failed')  return d.email ? `${d.email}${d.reason ? ` · ${d.reason}` : ''}` : '';
    if (action === 'prediction.saved')   return `${d.matchId} · ${d.homeScore}–${d.awayScore}${d.joker ? ' · 2×' : ''}`;
    if (action === 'admin.result_set')         return `${d.matchId} · ${d.homeScore}–${d.awayScore}`;
    if (action === 'admin.knockout_teams_set') return `${d.matchId} · ${d.home} vs ${d.away}`;
    if (action === 'admin.league_created') return `${d.name} (${d.leagueId})`;
    if (action === 'admin.league_member_invited') return `${d.email} → ${d.leagueName}${d.isNewUser ? '' : ' (existing)'}`;
    if (action === 'admin.league_member_added') return `userId:${d.targetUserId} → ${d.leagueId}`;
    if (action === 'admin.league_member_removed') return `userId:${d.targetUserId} ← ${d.leagueId}`;
    if (action === 'admin.league_renamed') return `${d.leagueId} → "${d.name}"`;
    if (action === 'admin.league_deleted') return `${d.leagueName} (${d.leagueId})`;
    if (action === 'admin.lock_toggled') return d.locked ? 'Locked' : 'Unlocked';
    if (action === 'admin.user_invited') return d.email || '';
    if (action === 'admin.user_role_changed') return `→ ${d.role === 'league-admin' ? 'League Admin' : d.role === 'admin' ? 'Global Admin' : 'Player'}`;
    if (action === 'winner.set')         return d.winner + (d.changedBeforeR32 ? ' (changed)' : '');
    if (action === 'default.set')        return `${d.homeScore}–${d.awayScore}`;
    if (action === 'profile.updated')    return d.name ? `→ ${d.name}` : '';
    if (action === 'admin.results_uploaded' || action === 'admin.predictions_uploaded')
      return `${d.saved} saved${d.errorCount ? `, ${d.errorCount} error(s)` : ''}`;
    if (action === 'results.auto_synced') return `${d.updated} updated, ${d.errors} error(s)`;
    return Object.entries(d)
      .filter(([, v]) => v !== null && v !== '' && v !== undefined)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ');
  } catch {
    return detail || '';
  }
}

function AuditRow({ record }) {
  const cfg = ACTION_CONFIG[record.action] ?? { label: record.action, category: 'game', color: 'var(--text-muted)' };
  const detail = formatDetail(record.action, record.detail);
  const ts = record.ts ? new Date(record.ts) : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.25rem 1rem', padding: '0.55rem 0', borderBottom: '1px solid var(--line)', alignItems: 'start' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', minWidth: 0 }}>
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: '5px', display: 'inline-block' }} />
        <span style={{ minWidth: 0 }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>{record.name || 'Unknown'}</strong>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}> — </span>
          <span style={{ color: cfg.color, fontSize: 'var(--t-sm)' }}>{cfg.label}</span>
          {detail && <span style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}> · {detail}</span>}
        </span>
      </div>
      <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-2xs)', whiteSpace: 'nowrap', flexShrink: 0 }}>
        <div style={{ color: 'var(--text-muted)' }}>{ts ? format(ts, 'dd MMM HH:mm:ss') : '—'}</div>
        {record.ip && <div style={{ color: 'var(--text-faint)', marginTop: '1px' }}>{record.ip}</div>}
      </div>
    </div>
  );
}

const FILTERS = [['all', 'All'], ['auth', 'Auth'], ['game', 'Game'], ['admin', 'Admin'], ['system', 'System']];
const PAGE_SIZE = 25;

export default function Audit() {
  const api = useApi();
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    api.adminGetAudit(500)
      .then(({ records: r }) => setRecords(r))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const filtered = records?.filter(r => filter === 'all' || actionCategory(r.action) === filter) ?? [];
  const shown = filtered.slice(0, visible);
  const remaining = filtered.length - visible;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>Admin</div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            {records ? `${records.length} events loaded` : 'Loading…'}
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => {
          setLoading(true);
          api.adminGetAudit(500)
            .then(({ records: r }) => setRecords(r))
            .catch(() => {})
            .finally(() => setLoading(false));
        }} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="chip-strip" style={{ marginBottom: '1rem' }}>
        {FILTERS.map(([val, label]) => {
          const count = records?.filter(r => val === 'all' || actionCategory(r.action) === val).length;
          return (
            <button key={val} className={`chip${filter === val ? ' active' : ''}`}
              onClick={() => { setFilter(val); setVisible(PAGE_SIZE); }}>
              {label}{records != null ? ` ${count}` : ''}
            </button>
          );
        })}
      </div>

      <div className="card">
        {loading && !records && (
          <div style={{ padding: '1rem 0', color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>Loading…</div>
        )}
        {records && filtered.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>No records for this filter.</p>
        )}
        {records && filtered.length > 0 && (
          <>
            <div>{shown.map((r, i) => <AuditRow key={i} record={r} />)}</div>
            {remaining > 0 && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginTop: '0.75rem', width: '100%', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}
                onClick={() => setVisible(v => v + PAGE_SIZE)}
              >
                Show {Math.min(PAGE_SIZE, remaining)} more · {remaining} remaining
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
